<script lang="ts" setup>
import { useDefaultStore } from "@/store";
import api from "@/api";
import { useToast } from "primevue/usetoast";
import { $t } from "@/i18n";
import { debounce } from "@/assets/js/helpers";
import { useConfirm } from "primevue/useconfirm";
// import type { IUserList, IPermissions } from "@/api/types";
import { useSessionStore } from "@/store/session";

definePage({
	name: "ManageGeoscopes",
	meta: {
		name: "Geoscopes Management",
	},
});

const confirm = useConfirm();
const toast = useToast();
const store = useDefaultStore();
const sessionStore = useSessionStore();
const searchValue = ref<string | null>(null);
const selectedProduct = ref();
const loadingDelete = ref(false);
const editingRows = ref([]);

const onRowEditSave = async (event) => {
	await editGeoscope(event.newData.gsc_id, event.newData.gsc_name);
};

const getGeoscopesDebounce = () => {
	debounce(store.getGeoscopes({ search: searchValue.value }), 300, true);
};

async function deleteGeoscope(gsc_id: number) {
	loadingDelete.value = true;
	const params = {
		gsc_id,
	};
	try {
		const res = await api.geoscopesRemove(params);
		toast.add({
			severity: "success",
			summary: $t("app.manageTags.success.deleteTag.title"),
			detail: $t("app.manageTags.success.deleteTag.msg"),
			life: 3000,
		});
		store.getGeoscopes({
			search: searchValue.value,
		});
		console.log(res.data.data);
	} catch (err: any) {
		toast.add({
			severity: "error",
			summary: $t("app.postForm.error.default.title"),
			detail: $t("app.postForm.error.default.msg"),
			life: 3000,
		});
		console.warn(err.message);
	} finally {
		loadingDelete.value = false;
	}
}

const confirmDelete = (gsc_id: number) => {
	confirm.require({
		message: $t("app.manageTags.deleteTag.msg"),
		header: $t("app.manageTags.deleteTag.header"),
		rejectLabel: $t("app.basic.cancel"),
		rejectProps: {
			label: $t("app.basic.cancel"),
			severity: "secondary",
			outlined: true,
		},
		acceptProps: {
			label: $t("app.basic.delete"),
			severity: "danger",
		},
		accept: async () => {
			await deleteGeoscope(gsc_id);
		},
	});
};

async function editGeoscope(gsc_id: number, gsc_name: string | null = null) {
	const params = {
		gsc_id,
		gsc_name,
	};

	try {
		const res = await api.geoscopesEdit(params);
		toast.add({
			severity: "success",
			summary: $t("app.manageTags.success.editLabel.title"),
			detail: $t("app.manageTags.success.editLabel.msg"),
			life: 3000,
		});
		store.getGeoscopes({
			search: searchValue.value,
		});
		console.warn(res.data);
	} catch (err: any) {
		toast.add({
			severity: "error",
			summary: $t("app.postForm.error.default.title"),
			detail: $t("app.postForm.error.default.msg"),
			life: 3000,
		});
		console.warn(err.message);
	}
}
</script>

<template>
	<div class="admin-section-outer-wrapper">
		<div class="admin-section-inner-wrapper">
			<div class="admin-section-header-wrapper">
				<div class="admin-section-header-heading">
					{{ $t("app.manageTags.title") }}
				</div>
				<div class="admin-section-header-filters-wrapper">
					<IconField
						:pt="{
							root: {
								style: 'width: 333px; max-width: 90%; height: 38px;',
							},
						}"
					>
						<InputIcon>
							<i-fa-magnifying-glass />
						</InputIcon>
						<InputText
							v-model="searchValue"
							@value-change="getGeoscopesDebounce"
							:placeholder="$t('app.manageTags.searchPlaceholder')"
							fluid
						/>
					</IconField>
					<create-geoscope @create-geoscope="store.getGeoscopes()" />
				</div>
			</div>
			<div
				class="admin-section-content-wrapper"
				style="max-width: 600px"
			>
				<DataTable
					v-if="!store.geoscopeList"
					:value="[
						{ id: 1 },
						{ id: 2 },
						{ id: 3 },
						{ id: 4 },
						{ id: 5 },
						{ id: 6 },
						{ id: 7 },
						{ id: 8 },
						{ id: 9 },
						{ id: 10 },
					]"
					stripedRows
					scrollable
					scrollHeight="500px"
				>
					<Column
						:header="$t('app.manageTags.nameLabel')"
						style="width: 100%"
					>
						<template #body>
							<Skeleton height="29px"></Skeleton>
						</template>
					</Column>
					<Column style="width: 82px">
						<template #body>
							<Button
								severity="danger"
								variant="outlined"
								rounded
							>
								<template #icon>
									<i-fa-trash-can></i-fa-trash-can>
								</template>
							</Button>
						</template>
					</Column>
				</DataTable>
				<DataTable
					v-else
					v-model:editingRows="editingRows"
					:value="store.geoscopeList"
					stripedRows
					selectionMode="single"
					v-model:selection="selectedProduct"
					scrollable
					scrollHeight="500px"
					:virtualScrollerOptions="{ itemSize: 50 }"
					editMode="row"
					@row-edit-save="onRowEditSave"
				>
					<Column
						field="gsc_name"
						:header="$t('app.manageTags.nameLabel')"
						style="width: 100%"
					>
						<template #body="{ data }">
							<span
								class="gsc-name"
								role="button"
								tabindex="0"
							>
								{{ data.gsc_name }} ({{ data.entities_count }})
							</span>
						</template>
						<template #editor="{ data, field }">
							<InputText v-model="data[field]" />
						</template>
					</Column>
					<Column
						v-if="sessionStore.can('edit-tag')"
						:rowEditor="true"
						style="min-width: 120px"
						bodyStyle="text-align:end"
					></Column>
					<Column
						v-if="sessionStore.can('delete-tag')"
						style="display: flex; justify-content: center; width: 82px"
					>
						<template #header><i-fa-trash-can></i-fa-trash-can></template>
						<template #body="{ data }">
							<Button
								severity="danger"
								variant="outlined"
								rounded
								@click="confirmDelete(data.gsc_id)"
								:loading="loadingDelete"
							>
								<template #icon>
									<i-fa-trash-can></i-fa-trash-can>
								</template>
							</Button>
						</template>
					</Column>
				</DataTable>
			</div>
		</div>
	</div>
</template>

<style lang="scss">
.p-virtualscroller-inline {
	&::-webkit-scrollbar {
		background-color: $scrollbar-color;
		width: 5px;
		height: 5px;
	}

	&::-webkit-scrollbar-thumb {
		background-color: $scrollbar-color-thumb;
		border-radius: 5px;
	}
}

.p-datatable-column-title {
	font-size: 11px;
	font-weight: 700;
	line-height: 12.89px;
	color: $text-color-lighter;
}
</style>
